import { Link, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import MenuIcon from "@rsuite/icons/Menu";
import ExitIcon from "@rsuite/icons/Exit";
import CloseIcon from "@rsuite/icons/Close";
import SidebarNav from "./SidebarNav";

export default function DashboardLayout() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-slate-100 text-start" dir={t("dir")}>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-950 text-white shadow-md">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between gap-3 px-3 sm:px-5">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20 lg:hidden"
              aria-label={t("Menu")}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon className="text-[18px] text-white" />
            </button>

            <Link to="/dashboard" className="flex items-center gap-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
              <img src="/icons/logo.svg" alt="Logo" className="h-5 w-auto sm:h-6" />
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-white/60 sm:inline">{t("Dashboard")}</span>
            <Link
              to="/logout"
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/10 px-3 text-sm text-white/90 transition hover:bg-rose-500/90 hover:text-white"
              title="Logout"
            >
              <ExitIcon className="text-[16px] text-white" />
              <span className="hidden sm:inline">خروج</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        <aside
          className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-64 shrink-0 flex-col overflow-hidden border-e border-slate-800/40 bg-slate-900 lg:flex"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.96)), url(/back.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="border-b border-white/10 px-4 py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-white/45">
              NanoSeal
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-white">{t("Dashboard")}</h2>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain pb-6">
            <SidebarNav />
          </div>
        </aside>

        <main className="min-w-0 flex-1 p-3 sm:p-5 lg:p-6">
          <div className="min-h-[calc(100vh-5.5rem)]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile drawer */}
      <div
        className={[
          "fixed inset-0 z-50 lg:hidden",
          mobileOpen ? "pointer-events-auto" : "pointer-events-none",
        ].join(" ")}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          className={[
            "absolute inset-0 bg-slate-950/50 transition-opacity duration-200",
            mobileOpen ? "opacity-100" : "opacity-0",
          ].join(" ")}
          aria-label="Close menu"
          onClick={closeMobile}
        />

        <aside
          className={[
            "absolute start-0 top-0 flex h-full w-[min(20rem,88vw)] flex-col bg-slate-900 shadow-2xl transition-transform duration-300 ease-out",
            mobileOpen
              ? "translate-x-0"
              : t("dir") === "rtl"
                ? "translate-x-full"
                : "-translate-x-full",
          ].join(" ")}
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(15,23,42,0.95), rgba(15,23,42,0.98)), url(/back.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          role="dialog"
          aria-modal="true"
          aria-label={t("Menu")}
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div>
              <p className="text-xs text-white/45">NanoSeal</p>
              <h2 className="text-base font-semibold text-white">{t("Menu")}</h2>
            </div>
            <button
              type="button"
              onClick={closeMobile}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Close"
            >
              <CloseIcon className="text-[16px] text-white" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto pb-6">
            <SidebarNav onNavigate={closeMobile} />
          </div>
        </aside>
      </div>
    </div>
  );
}
